import React, { useMemo, useState } from 'react';
import { useRouteMatch } from 'react-router';
import {
  Box,
  CalendarPickerIcon,
  DateRangeDropdown,
  FlexListPlaceholder,
  Inscription,
  PaginationBar,
  Preloader,
  SearchInput,
  SelectDropdown,
} from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { capitalize } from 'lodash';
import styled from 'styled-components';

import { ALL_OPTION, RAW_DATE_FORMAT } from '@/common/constants';
import ROUTES from '@/common/routes';
import { OptionKey } from '@/common/types';
import { useAllOptionsToggle, useCurrentOrganization } from '@/modules/core/hooks';
import { useListPagination } from '@/modules/employer/hooks';
import { useAuthStore } from '@/modules/user/stores';

import AttributesSelector from './AttributesSelector';
import LogItem from './LogItem';
import useGetActivityLogsQuery, { Actions } from './useGetActivityLogs.query';
import useGetAttributesListQuery from './useGetAttributesList.query';

const initPage = 1;
const defaultPerPage = 10;
const SORT = 'id,DESC';
const TEST_ID = 'ActivityLog';
export const employeeDomainType =
  'EMPLOYEE,ENROLLMENT,ACCOUNT,CARD,LETTER_OF_MEDICAL_NECESSITY,PAYMENT_METHOD,CHECK,INVESTMENTS,CIP,CARD_PURSE,CONTRIBUTION';
export const planDomainType = 'PLAN,PLAN_YEAR,PLAN_CONFIGS,PLAN_TERM_PROPERTIES';
export const healthPlanDomainType = 'HEALTH_PLAN';
export const organizationDomainType = 'ORGANIZATION,PLAN,PLAN_YEAR,HEALTH_PLAN';
export const expressSetupDomainType = 'EXPRESS_SETUP';
export const deductiblesDomainType = 'DEDUCTIBLES';
export const serviceDomainType = 'SERVICE';
export const rolesDomainType = 'ROLE';
export const copayDomainType = 'COPAY';
export const payoutDefinitionDomainType = 'PAYOUT_DEFINITION';

const EMPLOYEE_ID_KEY = 'employeeId';
const PLAN_ID_KEY = 'planId';
const HEALTH_PLAN_ID_KEY = 'healthPlanId';
const ORGANIZATION_ID_KEY = 'organizationId';
const ROLE_ID_KEY = 'roleId';

interface CalendarWrapperProps {
  disabled?: boolean;
}

const CalendarWrapper = styled.div<CalendarWrapperProps>`
  position: absolute;
  background: ${({ theme }) => theme.colors.canvas};
  top: 8px;
  right: 13px;
  font-size: 18px;
  margin-bottom: ${({ disabled }) => (disabled ? undefined : '3px')};
  svg {
    fill: ${({ theme }) => theme.colors.background1};
  }
`;

const LogItemWrapper = styled(Box)`
  > div:not(:last-child) {
    margin-bottom: ${({ theme }) => theme.spacings.spacing8};
  }
`;

const actions = [
  {
    key: Actions.create,
    value: Actions.create,
    title: capitalize(Actions.create),
  },
  {
    key: Actions.update,
    value: Actions.update,
    title: capitalize(Actions.update),
  },
  {
    key: Actions.delete,
    value: Actions.delete,
    title: capitalize(Actions.delete),
  },
];

const LogList = () => {
  const {
    path,
    params: { id },
  } = useRouteMatch<{ id: string }>();
  const { user } = useAuthStore();
  const {
    observingOrganization: { id: observingId },
  } = useCurrentOrganization();
  const isEmployeePage = useMemo(
    () => path === ROUTES.EMPLOYEE_VIEW() || path === ROUTES.EMPLOYEE_EDIT_VIEW(),
    [path]
  );
  const isPlansPage = useMemo(() => path === ROUTES.PLAN_SETUP || path === ROUTES.PLANS_VIEW, [path]);
  const isPlanTemplatesPage = useMemo(
    () => path === ROUTES.TEMPLATE_SETUP || path === ROUTES.TEMPLATE,
    [path]
  );
  const isHealthPlanPage = useMemo(
    () => path === ROUTES.HEALTH_PLAN || path === ROUTES.HEALTH_PLAN_SETUP,
    [path]
  );
  const isHealthPlanTemplatesPage = useMemo(
    () => path === ROUTES.HEALTH_PLAN_TEMPLATE_SETUP || path === ROUTES.HEALTH_PLAN_TEMPLATE,
    [path]
  );
  const isExpressSetupPage = useMemo(() => path === ROUTES.EXPRESS_SETUP_ACTIVITY_LOGS, [path]);
  const isDeductiblesSpendTrackerPage = useMemo(() => path === ROUTES.HEALTH_PLAN_SPENDS_DETAILS(), [path]);
  const isServicesPage = useMemo(() => path === ROUTES.SERVICES, [path]);
  const isRoleBuilderPage = useMemo(() => path === ROUTES.ROLE_EDIT(), [path]);
  const isCopayAdjudicationPage = useMemo(() => path === ROUTES.COPAY_ADJUDICATION, [path]);
  const isPayoutDefinitionPage = useMemo(
    () => path === ROUTES.PAYOUT_DEFINITION_VIEW_BY_ID() || path === ROUTES.PAYOUT_DEFINITION_EDIT_BY_ID(),
    [path]
  );

  const currentId = useMemo(
    () => (isCopayAdjudicationPage || isPayoutDefinitionPage ? observingId : id),
    [id, isCopayAdjudicationPage, isPayoutDefinitionPage, observingId]
  );

  const payload = useMemo(() => {
    if (isPayoutDefinitionPage) {
      return {
        domain_types: payoutDefinitionDomainType,
        payout_definition_id: id,
      };
    }
    if (isExpressSetupPage) {
      return {
        domain_types: expressSetupDomainType,
        lockbox_id: id,
      };
    }
    if (isServicesPage) {
      return {
        domain_types: serviceDomainType,
      };
    }
    if (isEmployeePage) {
      return {
        domain_types: employeeDomainType,
        employee_id: id,
      };
    }
    if (isPlansPage || isPlanTemplatesPage) {
      return {
        domain_types: planDomainType,
        plan_id: id,
      };
    }
    if (isHealthPlanPage || isHealthPlanTemplatesPage) {
      return {
        domain_types: healthPlanDomainType,
        entity_id: id,
      };
    }
    if (isDeductiblesSpendTrackerPage) {
      return {
        domain_types: deductiblesDomainType,
        employee_id: id,
      };
    }
    if (isRoleBuilderPage) {
      return {
        domain_types: rolesDomainType,
        role_id: id,
      };
    }
    if (isCopayAdjudicationPage) {
      return {
        domain_types: copayDomainType,
        organization_id: observingId,
      };
    }
    return {
      domain_types: organizationDomainType,
      organization_id: id,
    };
  }, [
    isExpressSetupPage,
    isServicesPage,
    isEmployeePage,
    isPlansPage,
    isPlanTemplatesPage,
    isHealthPlanPage,
    isHealthPlanTemplatesPage,
    isDeductiblesSpendTrackerPage,
    isRoleBuilderPage,
    isCopayAdjudicationPage,
    isPayoutDefinitionPage,
    id,
    observingId,
  ]);

  const { attributesList, isLoading: areAttributesLoading } = useGetAttributesListQuery(payload);
  const [expandedLogId, setExpandedLogId] = useState('');
  const [selectedAttributes, setSelectedAttributes] = useState<string[]>([]);
  const [selectedActions, setSelectedActions] = useState<OptionKey[]>([ALL_OPTION.value]);
  const actionValues = useAllOptionsToggle(selectedActions);
  const hasActions = useMemo(
    () => actionValues.length > 0 && !actionValues.some((action) => action === ALL_OPTION.value),
    [actionValues]
  );
  const [dateRange, setDateRange] = useState<Array<Date | null>>([]);
  const { page, perPage, setPerPage, setPage, currentSearch, searchString, setSearchString } =
    useListPagination(defaultPerPage);
  const isAllApplied = useMemo(
    () =>
      selectedAttributes.length === 0 ||
      selectedAttributes.length ===
        Object.values(attributesList.map((item) => item.attributes)).flat().length,
    [selectedAttributes, attributesList]
  );

  const idKey = useMemo(() => {
    if (isEmployeePage || isDeductiblesSpendTrackerPage) return EMPLOYEE_ID_KEY;
    if (isPlansPage || isPlanTemplatesPage) return PLAN_ID_KEY;
    if (isHealthPlanPage || isHealthPlanTemplatesPage) return HEALTH_PLAN_ID_KEY;
    if (isRoleBuilderPage) return ROLE_ID_KEY;
    return ORGANIZATION_ID_KEY;
  }, [
    isEmployeePage,
    isDeductiblesSpendTrackerPage,
    isPlansPage,
    isPlanTemplatesPage,
    isHealthPlanPage,
    isHealthPlanTemplatesPage,
    isRoleBuilderPage,
  ]);
  const { logs, total, isLoading } = useGetActivityLogsQuery({
    [idKey]: isServicesPage ? user?.organizationId : currentId,
    ...(isPayoutDefinitionPage ? { payoutDefinitionId: id } : {}),
    search: currentSearch,
    actions: hasActions ? (actionValues as string[]) : [],
    ...(isAllApplied ? {} : { fields: selectedAttributes }),
    ...(dateRange[0]
      ? {
          dateFrom: dayjs(dateRange[0]).format(RAW_DATE_FORMAT),
        }
      : {}),
    ...(dateRange[1]
      ? {
          dateTo: dayjs(dateRange[1]).format(RAW_DATE_FORMAT),
        }
      : {}),
    page,
    size: perPage,
    sort: SORT,
    domainType: payload.domain_types,
  });

  return (
    <Box background={{ color: 'module' }} round="moduleRound" data-testid={`${TEST_ID}_container`}>
      <Box align="center" data-testid={`${TEST_ID}_content`}>
        <Box width="100%" direction="row" justify="between" align="center" data-testid={`${TEST_ID}_toolbar`}>
          <Inscription
            weight="bold"
            color="textBody"
            style={{ whiteSpace: 'nowrap' }}
            data-testid={`${TEST_ID}_total`}
          >
            Activity log: {total}
          </Inscription>
          <Box direction="row" align="center" height="40px" data-testid={`${TEST_ID}_filters`}>
            <Box margin={{ left: 'spacing8' }} width={{ min: '230px' }} data-testid={`${TEST_ID}_search`}>
              <SearchInput
                testId={TEST_ID}
                value={searchString}
                hasSearchingHistory
                placeholder="Search"
                onChange={setSearchString}
                resetValue={searchString}
              />
            </Box>
            <Box
              margin={{ left: 'spacing12' }}
              style={{ position: 'relative' }}
              data-testid={`${TEST_ID}_attributes-filter`}
            >
              <AttributesSelector
                applyAttributes={(attributes: string[]) => {
                  setSelectedAttributes(attributes);
                  setPage(initPage);
                }}
                appliedAttributes={selectedAttributes}
                isAllApplied={isAllApplied}
                isLoading={areAttributesLoading}
                attributesList={attributesList}
              />
            </Box>
            <Box
              margin={{ left: 'spacing12' }}
              width={{ min: '160px', max: '160px' }}
              data-testid={`${TEST_ID}_action-filter`}
            >
              <SelectDropdown
                id="action"
                testId={`${TEST_ID}_action`}
                activeTitle
                ellipsisMode
                allMode
                name="Action"
                prefix="Action"
                options={[ALL_OPTION, ...actions]}
                values={actionValues}
                onChangeValues={(vals) => {
                  setSelectedActions(vals);
                  setPage(initPage);
                }}
              />
            </Box>
            <Box
              margin={{ left: 'spacing12' }}
              width={{ min: 'control' }}
              data-testid={`${TEST_ID}_date-filter`}
            >
              <DateRangeDropdown
                prefix="Period:"
                onChange={(range) => {
                  setDateRange(range);
                  setPage(initPage);
                }}
                ellipsisMode
                startDate={dateRange[0]}
                endDate={dateRange[1]}
                icon={
                  <CalendarWrapper>
                    <CalendarPickerIcon />
                  </CalendarWrapper>
                }
              />
            </Box>
          </Box>
        </Box>
        <Box
          data-testid={`${TEST_ID}_header`}
          margin={{ top: 'spacing12', bottom: 'spacing8' }}
          pad={{ horizontal: 'spacing24', vertical: '7px' }}
          background="legend"
          round="container2Round"
          width="100%"
          height="36px"
          direction="column"
          justify="center"
        >
          <Box
            data-testid={`${TEST_ID}_header-row`}
            style={{
              fontSize: '12px',
              fontWeight: 'bold',
              lineHeight: '18px',
              color: 'textOnColor',
            }}
            direction="row"
            height="18px"
          >
            <Box width="3%" data-testid={`${TEST_ID}_column_expand`} />
            <Box width="22%" pad={{ left: 'spacing16' }} data-testid={`${TEST_ID}_column_date`}>
              Date of change
            </Box>
            <Box width="22%" pad={{ left: 'spacing16' }} data-testid={`${TEST_ID}_column_entity`}>
              Entity
            </Box>
            <Box width="27%" pad={{ left: 'spacing16' }} data-testid={`${TEST_ID}_column_user`}>
              Author
            </Box>
            <Box width="18%" pad={{ left: 'spacing16' }} data-testid={`${TEST_ID}_column_source`}>
              Source of change
            </Box>
            <Box width="8%" pad={{ left: 'spacing16' }} data-testid={`${TEST_ID}_column_details`}>
              Changes
            </Box>
          </Box>
        </Box>
        {isLoading ? (
          <Box height="62px" justify="center">
            <Preloader />
          </Box>
        ) : (
          <LogItemWrapper width="100%" data-testid={`${TEST_ID}_rows`}>
            {logs?.map((data) => (
              <Box key={data.id}>
                <LogItem
                  data={data}
                  isExpanded={data.id === expandedLogId}
                  setExpandedLogId={setExpandedLogId}
                  isTwoColumnMode
                />
              </Box>
            ))}
          </LogItemWrapper>
        )}
        {total || isLoading ? (
          <PaginationBar
            testId={`${TEST_ID}_pagination`}
            page={page}
            total={total}
            pageSize={perPage}
            onChangePage={setPage}
            onChangePageSize={setPerPage}
          />
        ) : (
          <FlexListPlaceholder />
        )}
      </Box>
    </Box>
  );
};

export default LogList;
